import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import { useCallback } from "react";

import { DialogEnum } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { useUIContext } from "@/features/common/hooks/UIContext/useUIContext";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { PostDetailFragment } from "@/generated/generated-hooks";

type UseReportPostButtonArgs = {
  postDetail: Pick<
    PostDetailFragment,
    "_id" | "slug" | "writerId" | "title" | "hasCurrentUserReport"
  >;
  openReportPostDialog: () => void;
};

export const useReportPostButton = ({
  postDetail,
  openReportPostDialog,
}: UseReportPostButtonArgs) => {
  const { _id, title, writerId, hasCurrentUserReport, slug } = postDetail;
  const { t } = useTranslation("common");
  const { openDialog } = useUIContext();

  const { user } = useUserContext();

  const onClickReportPostButton = useCallback(() => {
    firebase.analytics().logEvent(AnalyticsEventEnum.MenuReportPost, {
      postId: _id,
      postSlug: slug,
      postTitle: title,
      writerId,
    });

    if (!user?._id) return openDialog(DialogEnum.Login);

    openReportPostDialog();
  }, [_id, slug, title, writerId, user?._id, openDialog, openReportPostDialog]);

  const reportButtonLabel = hasCurrentUserReport
    ? t("common:post.report.reportedMenuButtonLabel")
    : t("common:post.report.menuButtonLabel");

  return {
    reportButtonLabel,
    onClickReportPostButton,
    reportButtonDisabled: hasCurrentUserReport,
  };
};
